import React from "react";
import { T } from "../../../app/tokens.js";
import { parseNumericField } from "../../../utils/parseNumericField.js";
import { areaRentRatesError } from "../../../../shared/leaseAreaRentValidation.js";

export const rateDraft = (rate) => ({
  ...(rate.id ? { id: rate.id } : {}),
  annualRent: rate.annualRent == null ? "" : String(rate.annualRent),
  effectiveFrom: rate.effectiveFrom || "", effectiveTo: rate.effectiveTo || "",
  sourceCitation: rate.sourceCitation || "", provenance: rate.provenance || "",
});

export function parseRentRateDrafts(drafts) {
  const rates = drafts.map((draft) => {
    const text = String(draft.annualRent ?? "").trim();
    return { ...draft, annualRent: text === "" ? null
      : /^(?:\d+|\d{1,3}(?:,\d{3})+)(?:\.\d+)?$/.test(text) ? parseNumericField(text) : NaN };
  });
  return { rates, error: areaRentRatesError(rates) };
}

export function LeaseAreaRentEditor({ area, drafts, onChange, disabled, currency }) {
  const rows = drafts ?? (area.rentRates || []).map(rateDraft);
  const validationError = parseRentRateDrafts(rows).error;
  const update = (index, key, value) => onChange(area.id, "rentRates",
    rows.map((row, i) => i === index ? { ...row, [key]: value } : row));
  const inputStyle = { width: "100%", boxSizing: "border-box", padding: "5px 8px",
    border: `1px solid ${T.borderDark}`, borderRadius: 4, color: T.dark, background: T.white,
    fontSize: T.fsSm, fontFamily: "inherit" };
  return <div data-testid={`area-rent-editor-${area.id}`} style={{ marginTop: 10 }}>
    <div style={{ fontSize: T.fsXs, color: T.mid }}>
      Dated annual contractual rates ({currency}). Annual rate / 12 applies to a complete month.
      Blank rent means unknown; 0 means sourced zero. Dates are inclusive; no renewal or proration is assumed.
    </div>
    {rows.map((row, index) => <fieldset key={row.id || `new-${index}`} disabled={disabled}
      style={{ border: `1px solid ${T.border}`, borderRadius: 4, margin: "8px 0", padding: 8 }}>
      <legend style={{ fontSize: T.fsXs, color: T.mid }}>Rate segment {index + 1}</legend>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
        {[["annualRent", `Annual rent (${currency})`, "text"], ["effectiveFrom", "Effective from", "date"],
          ["effectiveTo", "Effective through", "date"]].map(([key, label, type]) => <label key={key}
          style={{ fontSize: T.fsXs, color: T.mid }}>{label}
          <input aria-label={`${area.name} segment ${index + 1} ${label}`} type={type} value={row[key]}
            onChange={(event) => update(index, key, event.target.value)} style={inputStyle} />
        </label>)}
      </div>
      {[["sourceCitation", "Source citation"], ["provenance", "Source provenance / review limits"]].map(([key, label]) =>
        <label key={key} style={{ display: "block", marginTop: 6, fontSize: T.fsXs, color: T.mid }}>{label}
          <input aria-label={`${area.name} segment ${index + 1} ${label}`} value={row[key]} maxLength={2000}
            onChange={(event) => update(index, key, event.target.value)} style={inputStyle} />
        </label>)}
      {!row.id && <button type="button" disabled={disabled} onClick={() =>
        onChange(area.id, "rentRates", rows.filter((_, i) => i !== index))}
      style={{ marginTop: 6, fontSize: T.fsXs, color: T.mid }}>Remove unsaved segment</button>}
    </fieldset>)}
    {validationError && <div role="alert" data-testid={`area-rent-validation-${area.id}`}
      style={{ color: T.ragRed, fontSize: T.fsXs, margin: "6px 0" }}>
      {validationError}
    </div>}
    <button type="button" disabled={disabled} onClick={() =>
      onChange(area.id, "rentRates", [...rows, rateDraft({})])}
    style={{ marginTop: 6, fontSize: T.fsSm, color: T.dark }}>Add dated rent rate · {area.name}</button>
  </div>;
}
